// Setup › DE subscriptions (`/c/{slug}/setup/subscriptions`; design spec 2026-10-19 §9, coverage
// rows 61–63): every DE's monthly fee, with "New subscription" and a per-row "Edit". Two calls on
// load: the subscriptions and the DEs (their names, and the seats a new subscription goes on);
// the company's currency is read only when the create dialog opens.
import { EmptyState, workerName } from "@companyos/ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { CreditCardIcon, PlusIcon } from "lucide-react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { SubscriptionDialog } from "../de/subscription-dialog";
import { SubscriptionsTable } from "../de/subscriptions-tab";
import { digitalEmployeesQuery, subscriptionsQuery } from "../queries";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { SetupPage } from "./setup-page";

export function SetupSubscriptions({ api, company }: { api: Api; company: CompanyAccess }) {
  const subs = useInfiniteQuery(subscriptionsQuery(api, company.id));
  const des = useInfiniteQuery(digitalEmployeesQuery(api, company.id));
  const deRows = des.data?.pages.flatMap((p) => p.data) ?? [];
  const names = new Map(deRows.map((d) => [d.membership_id, d.display_name]));
  // Retired DEs have left their seat; a new subscription needs one.
  const seated = deRows.flatMap((d) =>
    d.seat && d.status !== "retired"
      ? [{ id: d.membership_id, name: workerName(d.display_name), seatId: d.seat.id }]
      : [],
  );
  const create = seated.length ? (
    <SubscriptionDialog
      api={api}
      companyId={company.id}
      operator={company.roles.includes("agency_operator")}
      des={seated}
      trigger={{ label: "New subscription", size: "sm", icon: PlusIcon }}
    />
  ) : null;
  const rows = subs.data?.pages.flatMap((p) => p.data) ?? [];
  return (
    <SetupPage
      title="DE subscriptions"
      {...(subs.data ? { count: rows.length } : {})}
      actions={rows.length ? create : null}
    >
      {subs.isPending || des.isPending ? (
        <Loading label="Loading subscriptions" rows={3} />
      ) : subs.isError ? (
        <LoadError what="DE subscriptions" error={subs.error} />
      ) : des.isError ? (
        <LoadError what="Digital employees" error={des.error} />
      ) : !rows.length ? (
        <EmptyState
          variant="card"
          icon={CreditCardIcon}
          title="No DE subscriptions yet"
          description={seated.length ? undefined : "Set up a digital employee on a seat first."}
          actions={create}
        />
      ) : (
        <div className="flex min-w-0 flex-col gap-3">
          <SubscriptionsTable
            api={api}
            company={company}
            rows={rows}
            deName={(id) => names.get(id) ?? `DE …${id.slice(-4)}`}
            caption="DE subscriptions."
          />
          <LoadMore
            what="subscriptions"
            hasMore={subs.hasNextPage}
            loading={subs.isFetchingNextPage}
            onMore={() => void subs.fetchNextPage()}
          />
        </div>
      )}
    </SetupPage>
  );
}
